/**
 * Handles native push notification registration, foreground display, and deep linking.
 * Only active when user is authenticated as member or trainer on a native platform.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { initNativePush } from '../utils/nativePushNotifications';
import { authAPI } from '../services/api';
import { fetchUnreadCount } from '../store/slices/chatSlice';
import { selectIsAuthenticated, selectUserRole } from '../store/slices/authSlice';

const PUSH_TOKEN_KEY = 'push_device_token';

/**
 * Clear the OS notification tray (delivered while the app was in background or
 * killed) and reset the app badge to 0 when the app is foregrounded.
 *
 * The notification inbox is now server-backed (notificationsAPI), so delivered
 * pushes are already persisted server-side at send time — we no longer copy
 * tray items into device-local storage. This only performs the tray/badge reset.
 */
async function sweepDeliveredNotifications() {
  try {
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    if (!notifications?.length) return;
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (err) {
    console.warn('[PUSH] Failed to sweep delivered notifications:', err);
  }
}

export default function NativePushHandler() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);

  const navigateRef = useRef(navigate);
  const dispatchRef = useRef(dispatch);
  const userRoleRef = useRef(userRole);
  // Dedupe window for token POSTs so the several sources that can fire a token
  // (initial window.__fcmToken check, the 0/2/5/10s native bridge retries, and
  // the per-foreground re-register below) collapse into ~one backend write per
  // token. Persists across effect re-runs so it survives auth transitions.
  const lastTokenPostRef = useRef({ token: null, at: 0 });
  navigateRef.current = navigate;
  dispatchRef.current = dispatch;
  userRoleRef.current = userRole;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isAuthenticated || (userRole !== 'member' && userRole !== 'trainer' && userRole !== 'administrator')) {
      console.log('[PUSH] Skipping: not authenticated or unsupported role:', { isAuthenticated, userRole });
      return;
    }

    let cancelled = false;
    console.log('[PUSH] Initializing push for role:', userRole);

    // Idempotent POST of the current device token to the backend (upsert keyed
    // on the token). Deduped within an 8s window so the multiple sources that
    // can surface the same token don't spam the endpoint; a failed POST clears
    // the dedupe so the next trigger retries.
    const registerToken = async (token) => {
      if (cancelled || !token) return;
      const now = Date.now();
      const last = lastTokenPostRef.current;
      if (last.token === token && now - last.at < 8000) return;
      lastTokenPostRef.current = { token, at: now };
      try {
        const platform = Capacitor.getPlatform();
        const language = i18n.language?.split('-')[0] || 'hr';
        await authAPI.registerDeviceToken({ token, platform: platform || 'ios', language });
        console.log('[PUSH] Token registered with backend OK');
        localStorage.setItem(PUSH_TOKEN_KEY, token);
      } catch (err) {
        lastTokenPostRef.current = { token: null, at: 0 };
        console.error('[PUSH] Token registration failed:', err?.response?.status, err?.response?.data || err.message);
      }
    };

    initNativePush({
      onTokenReceived: (token) => {
        if (cancelled) return;
        console.log('[PUSH] FCM token to register:', token?.substring(0, 30) + '...');
        registerToken(token);
      },
      onNotificationReceived: () => {
        if (cancelled) return;
        // No toast on native - iOS/Android show their own native banners.
        // The inbox is server-backed (persisted at send time), so we only
        // refresh the chat unread count here; the bell polls the server itself.
        dispatchRef.current(fetchUnreadCount());
      },
      onNotificationTapped: (notification) => {
        if (cancelled) return;
        const data = notification.data || {};
        const type = data.type;
        // Inbox rows are written server-side at send time — nothing to store here.
        const role = userRoleRef.current;

        // --- Admin deep links ---
        if (role === 'administrator') {
          if (type === 'new_member' || type === 'new_trainer') {
            navigateRef.current('/admin', { state: { tab: 'users' } });
          } else if (type === 'purchase') {
            navigateRef.current('/admin', { state: { tab: 'orders' } });
          } else {
            navigateRef.current('/admin');
          }
          return;
        }

        // --- Trainer deep links ---
        if (role === 'trainer') {
          if ((type === 'chat' || type === 'new_chat_message') && data.chatId) {
            navigateRef.current('/trainer', { state: { openChatWithMemberId: data.memberId || undefined } });
          } else if (type === 'new_member' || type === 'new_member_registered') {
            navigateRef.current('/trainer', { state: { openMembers: true } });
          } else if (type === 'purchase' || type === 'new_purchase_cash' || type === 'new_product_claim') {
            navigateRef.current('/trainer', { state: { openShopOrders: true } });
          } else if (type === 'workout_complete' || type === 'hiit_completed' || type === 'progress_photo') {
            navigateRef.current('/trainer', { state: { viewMemberId: data.memberId || undefined } });
          } else if (type === 'challenge') {
            navigateRef.current('/trainer', { state: { openChallenges: true } });
          } else {
            navigateRef.current('/trainer');
          }
          return;
        }

        // --- Member deep links ---
        if ((type === 'chat' || type === 'new_chat_message') && data.chatId) {
          navigateRef.current('/member', { state: { openChat: true } });
        } else if (type === 'session_assigned' || type === 'hiit_assigned' || type === 'program_assigned') {
          navigateRef.current('/member', { state: { tab: 'training' } });
        } else if (type === 'workout' && (data.programId || data.enrollmentId)) {
          navigateRef.current('/member', { state: { tab: 'training' } });
        } else if (type === 'new_product' || type === 'new_product_available') {
          navigateRef.current('/member', { state: { tab: 'shop' } });
        } else if (type === 'cash_confirmed' || type === 'payment_confirmed' || type === 'purchase') {
          navigateRef.current('/member', { state: { tab: 'purchases' } });
        } else if (type === 'membership_updated') {
          navigateRef.current('/member');
        } else if (type === 'post') {
          navigateRef.current('/member', { state: { tab: 'feed' } });
        } else if (type === 'challenge') {
          navigateRef.current('/member', { state: { tab: 'challenges' } });
        } else if (type === 'nutrition' || type === 'meal_plan_assigned') {
          navigateRef.current('/member', { state: { tab: 'nutrition' } });
        } else {
          navigateRef.current('/member');
        }
      },
      onPermissionDenied: () => {
        if (cancelled) return;
        toast(
          t('push.permissionDenied', 'Obavijesti su isključene. Uključite ih u postavkama telefona da biste primali poruke i obavijesti.'),
          { id: 'push-permission', duration: 6000, icon: '🔔' }
        );
      },
      onError: (err) => {
        if (cancelled) return;
        console.error('[PUSH] Registration error surfaced:', err);
        toast.error(
          t('push.registrationError', 'Obavijesti se trenutno ne mogu uključiti. Pokušajte ponovo kasnije.'),
          { id: 'push-registration', duration: 5000 }
        );
      }
    });

    // Sweep notifications already in the OS tray (delivered while app was killed/background).
    // This captures notifications the user hasn't tapped on.
    sweepDeliveredNotifications();

    const onVisible = () => {
      if (document.visibilityState !== 'visible' || cancelled) return;
      sweepDeliveredNotifications();

      // Re-register the device token on EVERY foreground / app-open.
      //
      // Cold opens already re-run this effect (verifyToken flips isAuthenticated
      // false→true), but a WARM RESUME (background→foreground, no JS reload) does
      // NOT — deps are unchanged. Without this, a user who essentially never logs
      // out (the trainer) would never re-POST his FCM token, so once that token
      // rotates (new build install) or gets server-deleted after an UNREGISTERED
      // send, his DeviceToken rows stay empty forever and pushes stop — while
      // members, who log in/out often, keep self-healing via the login path.
      //
      // The server upsert is idempotent and registerToken() dedupes, so firing
      // this on every resume is safe.
      if (!Capacitor.isNativePlatform()) return;
      // Nudge the native layer to (re)deliver the current token. On Android this
      // re-fires the 'registration' listener → onTokenReceived. On iOS the
      // AppDelegate re-fetches+dispatches window.__fcmToken on activation anyway;
      // register() is idempotent and does not prompt.
      try { PushNotifications.register(); } catch (_) { /* ignore */ }
      // iOS exposes the current FCM token synchronously via the native bridge —
      // re-POST it now instead of waiting on the async event round-trip.
      if (window.__fcmToken) registerToken(window.__fcmToken);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      import('../utils/nativePushNotifications').then(({ cleanupNativePush }) => {
        cleanupNativePush();
      }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userRole]);

  return null;
}

export { PUSH_TOKEN_KEY };
